/* CONTEXT:START
Wave: 1
Status: implemented
Stub-Type: N/A
Dependencies: [AuthContext, apiService, MonthYearPicker, AddSubmissionButton, SubmissionList, JWTDebugPanel]
Risk-Level: low
Validation-Method: unit-test
Performance-Target: <2s
Last-Updated: 2025-08-11
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import { Typography, Box, Alert, Grid } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Submission } from '../types';
import { apiService } from '../services/api';
import MonthYearPicker from './submissions/MonthYearPicker';
import AddSubmissionButton from './submissions/AddSubmissionButton';
import SubmissionList from './submissions/SubmissionList';
import { JWTDebugPanel } from './JWTDebugPanel';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);

  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);

    const response = await apiService.getUserSubmissions(month, year);

    if (response.error) {
      // ErrorResponse has structure: { error: { code, message }, timestamp, requestId }
      const errorMessage = response.error.error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      setSubmissions([]);
    } else {
      // The API returns data.data.submissions due to double nesting
      setSubmissions(response.data?.data?.submissions || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadSubmissions();
  }, [month, year]);

  const handleSubmissionChange = () => {
    loadSubmissions();
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        Welcome, {user?.displayName}!
      </Typography>
      
      {/* JWT Debug Panel - Show when there are API errors */}
      <JWTDebugPanel 
        isOpen={debugPanelOpen || !!error} 
        onToggle={() => setDebugPanelOpen(!debugPanelOpen)}
      />
      
      <Grid container spacing={4} sx={{ width: '100%' }}>
        {/* Left Column - Controls */}
        <Grid item xs={12} lg={3} xl={3}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            <MonthYearPicker
              month={month}
              year={year}
              onMonthChange={setMonth}
              onYearChange={setYear}
            />

            <Box sx={{ mt: 3 }}>
              <AddSubmissionButton
                month={month}
                year={year}
                onSubmissionCreated={handleSubmissionChange}
              />
            </Box>
          </Box>
        </Grid>

        {/* Right Column - Submissions List */}
        <Grid item xs={12} lg={9} xl={9}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <SubmissionList
            submissions={submissions}
            loading={loading}
            onSubmissionUpdated={handleSubmissionChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;